import { VersionBanner } from '@worklogix/web-common';
import { useEffect } from 'react';

import { ClockedInList } from './components/ClockedInList';
import { PunchEchoCard } from './components/PunchEchoCard';
import { StatusBar } from './components/StatusBar';
import { PairingScreen } from './device/PairingScreen';
import { useDeviceToken } from './device/useDeviceToken';
import { useCamera } from './photo/useCamera';
import { PinPad } from './pinpad/PinPad';
import { useClockSkew } from './skew/useClockSkew';
import { kioskEngine } from './sync/syncEngine';
import { useKioskState } from './sync/useKioskState';

/** The live clock screen — only mounted once the device is paired. */
function ClockScreen(): JSX.Element {
  const state = useKioskState();
  const skew = useClockSkew();
  // Warm front-facing stream only when the tenant has photo-on-punch enabled.
  const camera = useCamera(state.photoEnabled);

  useEffect(() => {
    kioskEngine.start();
    return () => {
      kioskEngine.stop();
    };
  }, []);

  const handlePin = (pin: string): void => {
    // Pass the capturer ONLY when enabled; the engine grabs one frame at submit
    // and NEVER lets capture block or delay the punch.
    void kioskEngine.submitPin(pin, state.photoEnabled ? camera.capture : undefined);
  };

  return (
    <div className="kiosk">
      <header className="kiosk__header">
        <div className="kiosk__dept">{state.departmentName ?? 'WorkLogix Clock'}</div>
        <StatusBar
          online={state.online}
          syncing={state.syncing}
          serverReachable={state.serverReachable}
          queuedCount={state.queuedCount}
          skewWarn={skew.warn}
          skewMs={skew.skewMs}
        />
      </header>

      <div className="kiosk__body">
        <div className="card">
          {state.photoEnabled && (
            <video
              ref={camera.videoRef}
              className="kiosk__camera"
              autoPlay
              muted
              playsInline
              aria-hidden="true"
            />
          )}
          {state.photoWarning !== null && (
            <div className="echo echo--warn" role="alert" aria-live="assertive">
              {state.photoWarning}
            </div>
          )}
          <PinPad onSubmit={handlePin} />
          <PunchEchoCard echo={state.echo} />
        </div>
        <ClockedInList entries={state.clockedIn} />
      </div>

      <VersionBanner />
    </div>
  );
}

/**
 * Root of the kiosk PWA. Gates on the paired device token: no token => pairing
 * screen; paired => the live clock. There is deliberately NO MSAL AuthProvider
 * and NO Entra sign-in — this is a device+PIN app.
 */
export function KioskApp(): JSX.Element {
  const deviceToken = useDeviceToken();
  return deviceToken === null ? <PairingScreen /> : <ClockScreen />;
}
